import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeColorsKeys } from '@agria/theme/src/styles/tokens/colors';
import { ThemeSpaceKeys } from '@agria/theme/src/styles/tokens/space';
import { useBackgroundColor } from '@agria/theme/src/utils';
import { SectionContextProvider } from '@agria/theme/src/context/SectionContext';
import { Container } from '@agria/theme/src/components/container/Container';
import { Stack } from '@agria/theme/src/components/stack/Stack';

// Interface

export interface SectionProps {
  children: any;
  as?: any;
  backgroundColor?: ThemeColorsKeys;
  narrow?: boolean;
  gap?: ThemeSpaceKeys;
}

type StyledSectionProps = Pick<SectionProps, 'backgroundColor'>;

// Styles

const StyledSection = styled.div<StyledSectionProps>`
  ${({ theme: { space }, backgroundColor }) => css`
    padding: ${space.fluidXxLarge} 0;
    overflow: hidden;

    // Background and color

    ${backgroundColor && useBackgroundColor(backgroundColor)};

    // Spacing between sibling sections

    &[data-background-color=${backgroundColor}] + [data-background-color=${backgroundColor}] {
      padding-top: 0;
    }
  `};
`;

// JSX

export const Section: FC<React.PropsWithChildren<SectionProps>> = ({
  as = 'section',
  children,
  backgroundColor = 'utilityWhite',
  narrow,
  gap = 'fluidXLarge',
  ...rest
}) => (
  <StyledSection
    backgroundColor={backgroundColor}
    data-background-color={backgroundColor}
    as={as}
    {...rest}
  >
    <SectionContextProvider value={{ sectionBackground: backgroundColor }}>
      <Container narrow={narrow}>
        <Stack gap={gap}>{children}</Stack>
      </Container>
    </SectionContextProvider>
  </StyledSection>
);

export default Section;
